import { tableViews } from "../../common/components/table/agGrid/tableViews";
import { setStateData } from "../../common/helpers/state";
import { agentProductionTerms as apt, TimePeriods } from "../../constants";
import { useCommonStore } from "../../store/store";
import { getIntervalLastDates  } from "../components/helpers/savedAgents";
import {
    growthAnalysisTooltipsL90d,
    incDecYtdGrowthAnalysisTooltips,
    incDecLtmGrowthAnalysisTooltips,
    overviewToolTipsSA,
    contactToolTipsSA,
    listingProficiencyTooltipsSA
} from "../constants/agentProductionConstants";
import { views } from "../constants/savedAgents";
import {
    useContactInfoAdditionalHeaders,
    useGrowthAnalysisAdditionalHeaders,
    useOverviewAdditionalHeaders,
    useListingProfAdditionalHeaders
} from "./agGrid";
import { headerParentIndexesToRemoveByTabAndView, tabs } from "../constants/suggestedAgents";
import { getTableViewConfig } from "../helpers/agentProductionHelpers";
import { getDateRangeText } from '../../common/helpers/titles';

export const useSuggestedAgentsTable = ({state, setState, growthAnalysisPeriod, currentTabId, selectedListingViewId}) => {

    const isCoopAgents = currentTabId === tabs.coopAgents.id;
    const timeIntervals = useCommonStore(state => state.timeIntervals);
    const last12MonthsDates = timeIntervals?.length && getIntervalLastDates(timeIntervals, apt.annually);
    const dateRangesText = {
        currentSearch: getDateRangeText(last12MonthsDates),
        allMls: getDateRangeText(last12MonthsDates),
    };

    const setOverviewColumns = (overviewColumns) => {
        setStateData('overviewColumns', overviewColumns, setState);
    };

    const setNewProducersOverviewColumns = (newProducersOverviewColumns) => {
        setStateData('newProducersOverviewColumns', newProducersOverviewColumns, setState);
    };

    const setCoopAgentsOverviewColumns = (coopAgentsOverviewColumns) => {
        setStateData('coopAgentsOverviewColumns', coopAgentsOverviewColumns, setState);
    };

    const setOverviewColumnsIncDec = ( incDecOverviewColumns) => {
        setStateData('incDecOverviewColumns', incDecOverviewColumns, setState);
    };

    const setContactInfoColumns = (contactInfoColumns) => {
        setStateData('contactInfoColumns', contactInfoColumns, setState);
    };

    const setNewProducersContactInfoColumns = (newProducersContactInfoColumns) => {
        setStateData('newProducersContactInfoColumns', newProducersContactInfoColumns, setState);
    };

    const setGrowthAnalysisColumnsYtd = growthAnalysisColumnsYtd => {
        setStateData('growthAnalysisColumnsYtd', growthAnalysisColumnsYtd, setState);
    };

    const setGrowthAnalysisColumnsLtm = (growthAnalysisColumnsLtm) => {
        setStateData('growthAnalysisColumnsLtm', growthAnalysisColumnsLtm, setState);
    };

    const setGrowthAnalysisColumnsL90 = (growthAnalysisColumnsL90) => {
        setStateData('growthAnalysisColumnsL90', growthAnalysisColumnsL90, setState);
    };

    const setGrowthAnalysisColumnsLtmIncDec = (growthAnalysisColumnsLtmIncDec) => {
        setStateData('growthAnalysisColumnsLtmIncDec', growthAnalysisColumnsLtmIncDec, setState);
    };

    const setGrowthAnalysisColumnsYtdIncDec = (growthAnalysisColumnsYtdIncDec) => {
        setStateData('growthAnalysisColumnsYtdIncDec', growthAnalysisColumnsYtdIncDec, setState);
    };

    const setListingProficiencyColumns = (listingProficiencyColumns) => {
        setStateData('listingProficiencyColumns', listingProficiencyColumns, setState);
    };

    const isIncDecProducers = currentTabId === tabs.incProducers.id || currentTabId === tabs.decProducers.id;
    const growthAnalysisTableSettings = {
        [TimePeriods[apt.annually]]: {
            additionalTableHeaders: useGrowthAnalysisAdditionalHeaders(
                state.growthAnalysisColumnsLtm,
            ),
            className: 'growth-analysis',
            columns: isIncDecProducers ? state.growthAnalysisColumnsLtmIncDec : state.growthAnalysisColumnsLtm,
            tableView: isIncDecProducers ? tableViews.incDecGrowthAnalysisLtm : tableViews.growthAnalysisLtm,
            tooltips: incDecLtmGrowthAnalysisTooltips,
            setColumns: isIncDecProducers ? setGrowthAnalysisColumnsLtmIncDec : setGrowthAnalysisColumnsLtm
        },
        [TimePeriods[apt.YTD]]: {
            additionalTableHeaders: useGrowthAnalysisAdditionalHeaders(
                state.growthAnalysisColumnsYtd,
            ),
            className: 'growth-analysis',
            columns: isIncDecProducers ? state.growthAnalysisColumnsYtdIncDec : state.growthAnalysisColumnsYtd,
            tableView: isIncDecProducers ? tableViews.incDecGrowthAnalysisYtd : tableViews.growthAnalysisYtd,
            tooltips: incDecYtdGrowthAnalysisTooltips,
            setColumns: isIncDecProducers ? setGrowthAnalysisColumnsYtdIncDec : setGrowthAnalysisColumnsYtd
        },
        [TimePeriods[apt.last90Days]]: {
            additionalTableHeaders: useGrowthAnalysisAdditionalHeaders(
                state.growthAnalysisColumnsL90,
            ),
            className: 'growth-analysis',
            columns: state.growthAnalysisColumnsL90,
            tableView: tableViews.growthAnalysisL90,
            tooltips: growthAnalysisTooltipsL90d,
            setColumns: setGrowthAnalysisColumnsL90
        }
    }?.[growthAnalysisPeriod];

    const headerParentsToRemove = headerParentIndexesToRemoveByTabAndView?.[currentTabId]?.[selectedListingViewId];
    const isNewProducers = currentTabId === tabs.newProducers.id;

    const getOverviewColumns = () => {
        if (isNewProducers) return state.newProducersOverviewColumns;
        if (isCoopAgents) return state.coopAgentsOverviewColumns;
        if (isIncDecProducers) return state.incDecOverviewColumns;
        return state.overviewColumns;
    };

    const getOverviewTableView = () => {
        if (isCoopAgents) return tableViews.overviewCoopAgents;
        if (isIncDecProducers) return tableViews.incDecOverview;
        return tableViews.overview;
    }

    const getOverviewSetColumns = () => {
        if (isNewProducers) return setNewProducersOverviewColumns;
        if (isCoopAgents) return setCoopAgentsOverviewColumns;
        if (isIncDecProducers) return setOverviewColumnsIncDec;
        return setOverviewColumns;
    }
    const tableSettings = {
        [views.overview]: {
            additionalTableHeaders: useOverviewAdditionalHeaders(
                state.overviewColumns,
                state.rowDataByTab[currentTabId]?.results?.length,
                dateRangesText,
                isCoopAgents && apt.recentCoopTransactions,
                overviewToolTipsSA,
            ),
            className: 'overview',
            columns: getOverviewColumns(),
            tableView: getTableViewConfig(getOverviewTableView(), headerParentsToRemove),
            tooltips: overviewToolTipsSA,
            setColumns: getOverviewSetColumns(),
        },
        [views.contactInformation]: {
            additionalTableHeaders: useContactInfoAdditionalHeaders(
                state.contactInfoColumns,
                dateRangesText,
                contactToolTipsSA,
            ),
            className: 'contact-information',
            columns: isNewProducers ? state.newProducersContactInfoColumns : state.contactInfoColumns,
            tableView: getTableViewConfig(tableViews.contactInformation, headerParentsToRemove),
            tooltips: contactToolTipsSA,
            setColumns: isNewProducers ? setNewProducersContactInfoColumns : setContactInfoColumns,
        },
        [views.growthAnalysis]: growthAnalysisTableSettings,
        [views.listingProficiency]: {
            additionalTableHeaders: useListingProfAdditionalHeaders(state.listingProficiencyColumns, listingProficiencyTooltipsSA),
            className: 'listing-proficiency',
            columns: state.listingProficiencyColumns,
            tableView: tableViews.listingProficiency,
            tooltips: listingProficiencyTooltipsSA,
            setColumns: setListingProficiencyColumns,
        },
    };

    return tableSettings?.[selectedListingViewId];
};
